/** String helpers (Voca-style). Null/undefined coerced to empty string via toString. */

// --- Internal Helpers ---

/**
 * Coerces value to string, handling null/undefined.
 * @param {*} value - Value to coerce
 * @returns {string} String representation
 */
const toString = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return String(value);
};

/**
 * Checks if value is nil (null or undefined).
 * @param {*} value - Value to check
 * @returns {boolean}
 */
const isNil = (value) => value === null || value === undefined;

// --- Constants & Configuration ---
const FORCE_UPPER_CASE = new Set([
  // Directions
  "n",
  "s",
  "e",
  "w",
  "ne",
  "nw",
  "se",
  "sw",
  // US States
  "al",
  "ak",
  "az",
  "ar",
  "ca",
  "co",
  "ct",
  "de",
  "fl",
  "ga",
  "hi",
  "id",
  "il",
  "in",
  "ia",
  "ks",
  "ky",
  "la",
  "me",
  "md",
  "ma",
  "mi",
  "mn",
  "ms",
  "mo",
  "mt",
  "ne",
  "nv",
  "nh",
  "nj",
  "nm",
  "ny",
  "nc",
  "nd",
  "oh",
  "ok",
  "or",
  "pa",
  "ri",
  "sc",
  "sd",
  "tn",
  "tx",
  "ut",
  "vt",
  "va",
  "wa",
  "wv",
  "wi",
  "wy",
  // Canadian Provinces
  "ab",
  "bc",
  "mb",
  "nb",
  "nl",
  "ns",
  "nt",
  "nu",
  "on",
  "pe",
  "qc",
  "sk",
  "yt",
  // Other
  "po",
  "rr",
  "usa",
]);

// Word splitting regex pattern (handles camelCase, PascalCase, kebab-case, snake_case, spaces)
const WORD_PATTERN = /[A-Z]?[a-z]+|[A-Z]+(?=[A-Z][a-z]|\d|\W|$)|\d+/g;

// --- Exported Functions ---

/**
 * Converts the first character of subject to upper case.
 * If restToLower is true, converts the rest to lower case.
 * @param {string} [subject=''] - The string to capitalize
 * @param {boolean} [restToLower=false] - Convert rest to lower case
 * @returns {string} The capitalized string
 * @example
 * capitalize('apple') // => 'Apple'
 * capitalize('aPPLE', true) // => 'Apple'
 */
export const capitalize = (subject = "", restToLower = false) => {
  const str = toString(subject);
  if (str === "") return "";

  const first = str.charAt(0).toUpperCase();
  const rest = restToLower ? str.slice(1).toLowerCase() : str.slice(1);

  return first + rest;
};

/**
 * Converts the first character of subject to lower case.
 * @param {string} [subject=''] - The string to decapitalize
 * @returns {string} The decapitalized string
 * @example
 * decapitalize('Sun') // => 'sun'
 */
export const decapitalize = (subject = "") => {
  const str = toString(subject);
  if (str === "") return "";

  return str.charAt(0).toLowerCase() + str.slice(1);
};

/**
 * Converts subject to camelCase.
 * @param {string} [subject=''] - The string to convert
 * @returns {string} The camelCase string
 * @example
 * camelCase('bird flight') // => 'birdFlight'
 * camelCase('BirdFlight') // => 'birdFlight'
 * camelCase('-BIRD-FLIGHT-') // => 'birdFlight'
 */
export const camelCase = (subject = "") => {
  const str = toString(subject);
  const words = str.match(WORD_PATTERN);

  if (!words) return "";

  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      return index === 0 ? lower : capitalize(lower);
    })
    .join("");
};

/**
 * Converts subject to PascalCase.
 * @param {string} [subject=''] - The string to convert
 * @returns {string} The PascalCase string
 * @example
 * pascalCase('bird flight') // => 'BirdFlight'
 */
export const pascalCase = (subject = "") => {
  const str = toString(subject);
  const words = str.match(WORD_PATTERN);

  if (!words) return "";

  return words.map((word) => capitalize(word.toLowerCase())).join("");
};

/**
 * Converts subject to kebab-case (spinal-case).
 * @param {string} [subject=''] - The string to convert
 * @returns {string} The kebab-case string
 * @example
 * kebabCase('goodbye blue sky') // => 'goodbye-blue-sky'
 * kebabCase('GoodbyeBlueSky') // => 'goodbye-blue-sky'
 */
export const kebabCase = (subject = "") => {
  const str = toString(subject);
  const words = str.match(WORD_PATTERN);

  if (!words) return "";

  return words.map((word) => word.toLowerCase()).join("-");
};

/**
 * Converts subject to snake_case.
 * @param {string} [subject=''] - The string to convert
 * @returns {string} The snake_case string
 * @example
 * snakeCase('hello world') // => 'hello_world'
 * snakeCase('HelloWorld') // => 'hello_world'
 */
export const snakeCase = (subject = "") => {
  const str = toString(subject);
  const words = str.match(WORD_PATTERN);

  if (!words) return "";

  return words.map((word) => word.toLowerCase()).join("_");
};

/**
 * Converts subject to lower case.
 * @param {string} [subject=''] - The string to convert
 * @returns {string} The lower case string
 */
export const lowerCase = (subject = "") => toString(subject).toLowerCase();

/**
 * Converts subject to upper case.
 * @param {string} [subject=''] - The string to convert
 * @returns {string} The upper case string
 */
export const upperCase = (subject = "") => toString(subject).toUpperCase();

/**
 * Converts subject to title case.
 * @param {string} [subject=''] - The string to convert
 * @returns {string} The title case string
 * @example
 * titleCase('hello world') // => 'Hello World'
 */
export const titleCase = (subject = "") => {
  const str = toString(subject);

  return str.toLowerCase().replace(/(?:^|\s)\S/g, (char) => char.toUpperCase());
};

/**
 * Formats a listing / market-status string (e.g. Zillow `home_status`: `FOR_SALE`, `on_market`) for UI display.
 * Underscores become spaces, then {@link titleCase}. Empty and `N/A` normalize to `"N/A"`.
 *
 * @param {string} [raw=''] - Raw status from the API or store
 * @returns {string} Human-readable label
 */
export const formatListingStatusLabel = (raw = "") => {
  const str = toString(raw).trim();
  if (!str || str.toUpperCase() === "N/A") {
    return "N/A";
  }
  const formatted = titleCase(str.split("_").join(" "));
  return formatted || "N/A";
};

/**
 * Swaps the case of each character in subject.
 * @param {string} [subject=''] - The string to swap case
 * @returns {string} The swapped case string
 * @example
 * swapCase('Hello World') // => 'hELLO wORLD'
 */
export const swapCase = (subject = "") => {
  const str = toString(subject);

  return [...str]
    .map((char) => {
      const lower = char.toLowerCase();
      const upper = char.toUpperCase();
      return char === lower ? upper : lower;
    })
    .join("");
};

/**
 * Removes diacritical marks (accents) from subject.
 * @param {string} [subject=''] - The string to latinise
 * @returns {string} The latinised string
 * @example
 * latinise('café') // => 'cafe'
 * latinise('résumé') // => 'resume'
 */
export const latinise = (subject = "") => {
  const str = toString(subject);

  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

/**
 * Converts subject to a URL-friendly slug.
 * @param {string} [subject=''] - The string to slugify
 * @returns {string} The slugified string
 * @example
 * slugify('What a wonderful world') // => 'what-a-wonderful-world'
 * slugify('Café Latté') // => 'cafe-latte'
 */
export const slugify = (subject = "") => {
  const str = toString(subject);

  return latinise(str)
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/**
 * Strips HTML tags from subject.
 * @param {string} [subject=''] - The string to strip
 * @param {string|string[]} [allowableTags] - Tags to preserve (e.g., '<b><i>' or ['b', 'i'])
 * @param {string} [replacement=''] - String to replace stripped tags with
 * @returns {string} The stripped string
 * @example
 * stripTags('<span><a href="#">Summer</a> is nice</span>') // => 'Summer is nice'
 * stripTags('<b>Bold</b> text', ['b']) // => '<b>Bold</b> text'
 */
export const stripTags = (subject = "", allowableTags, replacement = "") => {
  const str = toString(subject);

  if (!allowableTags) {
    return str.replace(/<\/?[^>]+(>|$)/g, replacement);
  }

  // Normalize allowableTags to array of lowercase tag names
  const allowed = Array.isArray(allowableTags)
    ? allowableTags.map((tag) => tag.toLowerCase())
    : (allowableTags.match(/<([a-z0-9]+)/gi) || []).map((tag) => tag.slice(1).toLowerCase());

  return str.replace(/<\/?([^>\s]+)[^>]*>/gi, (match, tagName) => {
    return allowed.includes(tagName.toLowerCase()) ? match : replacement;
  });
};

/**
 * Strips the byte order mark (BOM) from the beginning of subject.
 * @param {string} [subject=''] - The string to strip from
 * @returns {string} The stripped string
 * @example
 * stripBom('\uFEFFhello') // => 'hello'
 */
export const stripBom = (subject = "") => {
  const str = toString(subject);

  return str.charAt(0) === "\uFEFF" ? str.slice(1) : str;
};

/**
 * Removes whitespace from both ends of subject.
 * @param {string} [subject=''] - The string to trim
 * @param {string} [chars] - Characters to trim (default: whitespace)
 * @returns {string} The trimmed string
 */
export const trim = (subject = "", chars) => {
  const str = toString(subject);

  if (chars === undefined) {
    return str.trim();
  }

  const escaped = chars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
  const pattern = new RegExp(`^[${escaped}]+|[${escaped}]+$`, "g");

  return str.replace(pattern, "");
};

/**
 * Removes whitespace from the start of subject.
 * @param {string} [subject=''] - The string to trim
 * @param {string} [chars] - Characters to trim (default: whitespace)
 * @returns {string} The trimmed string
 */
export const trimLeft = (subject = "", chars) => {
  const str = toString(subject);

  if (chars === undefined) {
    return str.trimStart();
  }

  const escaped = chars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
  const pattern = new RegExp(`^[${escaped}]+`, "g");

  return str.replace(pattern, "");
};

/**
 * Removes whitespace from the end of subject.
 * @param {string} [subject=''] - The string to trim
 * @param {string} [chars] - Characters to trim (default: whitespace)
 * @returns {string} The trimmed string
 */
export const trimRight = (subject = "", chars) => {
  const str = toString(subject);

  if (chars === undefined) {
    return str.trimEnd();
  }

  const escaped = chars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
  const pattern = new RegExp(`[${escaped}]+$`, "g");

  return str.replace(pattern, "");
};

/**
 * Pads subject from both sides to a given length.
 * @param {string} [subject=''] - The string to pad
 * @param {number} [length=0] - The target length
 * @param {string} [padStr=' '] - The padding string
 * @returns {string} The padded string
 * @example
 * pad('dog', 5) // => ' dog '
 * pad('bird', 6, '-') // => '-bird-'
 */
export const pad = (subject = "", length = 0, padStr = " ") => {
  const str = toString(subject);
  const len = Math.max(0, Math.floor(length));

  if (str.length >= len) return str;

  const padLength = len - str.length;
  const leftPad = Math.floor(padLength / 2);

  return str.padStart(str.length + leftPad, padStr).padEnd(len, padStr);
};

/**
 * Pads subject from the left to a given length.
 * @param {string} [subject=''] - The string to pad
 * @param {number} [length=0] - The target length
 * @param {string} [padStr=' '] - The padding string
 * @returns {string} The padded string
 * @example
 * padLeft('dog', 5) // => '  dog'
 */
export const padLeft = (subject = "", length = 0, padStr = " ") => {
  const str = toString(subject);

  return str.padStart(Math.max(0, Math.floor(length)), padStr);
};

/**
 * Pads subject from the right to a given length.
 * @param {string} [subject=''] - The string to pad
 * @param {number} [length=0] - The target length
 * @param {string} [padStr=' '] - The padding string
 * @returns {string} The padded string
 * @example
 * padRight('dog', 5) // => 'dog  '
 */
export const padRight = (subject = "", length = 0, padStr = " ") => {
  const str = toString(subject);

  return str.padEnd(Math.max(0, Math.floor(length)), padStr);
};

/**
 * Truncates subject to a specified length.
 * @param {string} [subject=''] - The string to truncate
 * @param {number} [length=30] - The max length
 * @param {string} [end='...'] - The ending to append
 * @returns {string} The truncated string
 * @example
 * truncate('Once upon a time', 7) // => 'Once...'
 */
export const truncate = (subject = "", length = 30, end = "...") => {
  const str = toString(subject);
  const len = Math.max(0, Math.floor(length));
  const suffix = toString(end);

  if (str.length <= len) return str;

  return str.substring(0, Math.max(0, len - suffix.length)) + suffix;
};

/**
 * Truncates subject to a specified length, avoiding word breaks.
 * @param {string} [subject=''] - The string to prune
 * @param {number} [length=30] - The max length
 * @param {string} [end='...'] - The ending to append
 * @returns {string} The pruned string
 * @example
 * prune('Once upon a time', 12) // => 'Once upon...'
 */
export const prune = (subject = "", length = 30, end = "...") => {
  const str = toString(subject);
  const len = Math.max(0, Math.floor(length));
  const suffix = toString(end);

  if (str.length <= len) return str;

  const maxLen = len - suffix.length;
  let truncated = str.substring(0, maxLen);

  // Only find word boundary if we're cutting mid-word
  // Check if the next character is a space or end of string
  if (maxLen < str.length && str.charAt(maxLen) !== " ") {
    const lastSpace = truncated.lastIndexOf(" ");
    if (lastSpace > 0) {
      truncated = truncated.substring(0, lastSpace);
    }
  }

  return truncated.trimEnd() + suffix;
};

/**
 * Checks whether subject is empty or contains only whitespace.
 * @param {string} [subject=''] - The string to check
 * @returns {boolean} True if blank, false otherwise
 * @example
 * isBlank('') // => true
 * isBlank('   ') // => true
 * isBlank('hello') // => false
 */
export const isBlank = (subject) => {
  if (isNil(subject)) return true;

  return /^\s*$/.test(toString(subject));
};

/**
 * Checks whether subject is empty.
 * @param {string} [subject=''] - The string to check
 * @returns {boolean} True if empty, false otherwise
 */
export const isEmpty = (subject) => {
  if (isNil(subject)) return true;

  return toString(subject) === "";
};

/**
 * Checks whether subject is a string.
 * @param {*} subject - The value to check
 * @returns {boolean} True if string, false otherwise
 */
export const isString = (subject) => typeof subject === "string";

/**
 * Checks whether subject contains only numeric characters.
 * @param {string} [subject=''] - The string to check
 * @returns {boolean} True if numeric, false otherwise
 * @example
 * isNumeric('123') // => true
 * isNumeric('12.5') // => true
 * isNumeric('abc') // => false
 */
export const isNumeric = (subject) => {
  if (isNil(subject) || subject === "") return false;

  const val = typeof subject === "string" ? subject.trim() : subject;

  return !isNaN(parseFloat(val)) && isFinite(val);
};

/**
 * Checks whether subject contains only digit characters.
 * @param {string} [subject=''] - The string to check
 * @returns {boolean} True if all digits, false otherwise
 * @example
 * isDigit('123') // => true
 * isDigit('12.5') // => false
 */
export const isDigit = (subject) => {
  if (isNil(subject)) return false;

  const str = toString(subject);

  return str !== "" && /^\d+$/.test(str);
};

/**
 * Checks whether subject contains only alpha characters.
 * @param {string} [subject=''] - The string to check
 * @returns {boolean} True if alpha, false otherwise
 */
export const isAlpha = (subject) => {
  if (isNil(subject)) return false;

  const str = toString(subject);

  return str !== "" && /^[a-zA-Z]+$/.test(str);
};

/**
 * Checks whether subject contains only alphanumeric characters.
 * @param {string} [subject=''] - The string to check
 * @returns {boolean} True if alphanumeric, false otherwise
 */
export const isAlphaDigit = (subject) => {
  if (isNil(subject)) return false;

  const str = toString(subject);

  return str !== "" && /^[a-zA-Z0-9]+$/.test(str);
};

/**
 * Checks whether subject is all lower case.
 * @param {string} [subject=''] - The string to check
 * @returns {boolean} True if lower case, false otherwise
 */
export const isLowerCase = (subject) => {
  if (isNil(subject)) return false;

  const str = toString(subject);

  return str !== "" && str === str.toLowerCase() && str !== str.toUpperCase();
};

/**
 * Checks whether subject is all upper case.
 * @param {string} [subject=''] - The string to check
 * @returns {boolean} True if upper case, false otherwise
 */
export const isUpperCase = (subject) => {
  if (isNil(subject)) return false;

  const str = toString(subject);

  return str !== "" && str === str.toUpperCase() && str !== str.toLowerCase();
};

/**
 * Checks whether subject starts with start.
 * @param {string} [subject=''] - The string to check
 * @param {string} start - The starting string
 * @param {number} [position=0] - The position to start searching
 * @returns {boolean}
 */
export const startsWith = (subject = "", start, position = 0) => {
  const str = toString(subject);

  return str.startsWith(toString(start), position);
};

/**
 * Checks whether subject ends with end.
 * @param {string} [subject=''] - The string to check
 * @param {string} end - The ending string
 * @param {number} [position] - The position to end searching
 * @returns {boolean}
 */
export const endsWith = (subject = "", end, position) => {
  const str = toString(subject);

  return str.endsWith(toString(end), position);
};

/**
 * Checks whether subject includes search.
 * @param {string} [subject=''] - The string to check
 * @param {string} search - The string to search for
 * @param {number} [position=0] - The position to start searching
 * @returns {boolean}
 */
export const includes = (subject = "", search, position = 0) => {
  const str = toString(subject);

  return str.includes(toString(search), position);
};

/**
 * Checks whether subject matches the pattern.
 * @param {string} [subject=''] - The string to check
 * @param {RegExp|string} pattern - The pattern to match
 * @param {string} [flags=''] - RegExp flags (when pattern is string)
 * @returns {boolean}
 * @example
 * matches('pluto', /plu.{2}/) // => true
 * matches('sun', 'S', 'i') // => true
 */
export const matches = (subject = "", pattern, flags = "") => {
  const str = toString(subject);
  const regex = pattern instanceof RegExp ? pattern : new RegExp(pattern, flags);

  return regex.test(str);
};

/**
 * Splits subject into an array of words.
 * @param {string} [subject=''] - The string to split
 * @param {string|RegExp} [pattern] - Custom pattern to match words
 * @returns {string[]} Array of words
 * @example
 * words('gravity can cross dimensions') // => ['gravity', 'can', 'cross', 'dimensions']
 * words('GravityCanCross') // => ['Gravity', 'Can', 'Cross']
 */
export const words = (subject = "", pattern) => {
  const str = toString(subject);

  if (str === "") return [];

  if (pattern) {
    const matches = str.match(pattern);
    return matches || [];
  }

  return str.match(WORD_PATTERN) || [];
};

/**
 * Splits subject into an array of characters.
 * @param {string} [subject=''] - The string to split
 * @returns {string[]} Array of characters
 */
export const chars = (subject = "") => [...toString(subject)];

/**
 * Splits subject into an array of graphemes (handles surrogate pairs).
 * @param {string} [subject=''] - The string to split
 * @returns {string[]} Array of graphemes
 */
export const graphemes = (subject = "") => {
  const str = toString(subject);

  // Use Intl.Segmenter if available for proper grapheme splitting
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    const segmenter = new Intl.Segmenter("en", { granularity: "grapheme" });
    return [...segmenter.segment(str)].map((segment) => segment.segment);
  }

  // Fallback: spread operator handles surrogate pairs but not all combining marks
  return [...str];
};

/**
 * Splits subject by separator.
 * @param {string} [subject=''] - The string to split
 * @param {string|RegExp} [separator=''] - The separator
 * @param {number} [limit] - Limit the number of splits
 * @returns {string[]} Array of chunks
 */
export const split = (subject = "", separator = "", limit) => {
  const str = toString(subject);

  return limit !== undefined ? str.split(separator, limit) : str.split(separator);
};

/**
 * Counts the number of characters in subject.
 * @param {string} [subject=''] - The string to count
 * @returns {number} The character count
 */
export const count = (subject = "") => [...toString(subject)].length;

/**
 * Counts the number of graphemes in subject.
 * @param {string} [subject=''] - The string to count
 * @returns {number} The grapheme count
 */
export const countGraphemes = (subject = "") => graphemes(subject).length;

/**
 * Counts the number of words in subject.
 * @param {string} [subject=''] - The string to count words in
 * @param {string|RegExp} [pattern] - Custom pattern to match words
 * @returns {number} The word count
 */
export const countWords = (subject = "", pattern) => words(subject, pattern).length;

/**
 * Counts occurrences of substring in subject.
 * @param {string} [subject=''] - The string to search in
 * @param {string} substring - The substring to count
 * @returns {number} The occurrence count
 */
export const countSubstrings = (subject = "", substring) => {
  const str = toString(subject);
  const sub = toString(substring);

  if (str === "" || sub === "") return 0;

  let count = 0;
  let pos = 0;

  while ((pos = str.indexOf(sub, pos)) !== -1) {
    count++;
    pos += sub.length;
  }

  return count;
};

/**
 * Returns the index of the first occurrence of search in subject.
 * @param {string} [subject=''] - The string to search in
 * @param {string} search - The string to search for
 * @param {number} [fromIndex=0] - The position to start searching
 * @returns {number} The index, or -1 if not found
 */
export const indexOf = (subject = "", search, fromIndex = 0) => {
  return toString(subject).indexOf(toString(search), fromIndex);
};

/**
 * Returns the index of the last occurrence of search in subject.
 * @param {string} [subject=''] - The string to search in
 * @param {string} search - The string to search for
 * @param {number} [fromIndex] - The position to start searching backwards
 * @returns {number} The index, or -1 if not found
 */
export const lastIndexOf = (subject = "", search, fromIndex) => {
  const str = toString(subject);

  return fromIndex === undefined ? str.lastIndexOf(toString(search)) : str.lastIndexOf(toString(search), fromIndex);
};

/**
 * Searches for a pattern in subject and returns the index.
 * @param {string} [subject=''] - The string to search in
 * @param {RegExp|string} pattern - The pattern to search for
 * @param {number} [fromIndex=0] - The position to start searching
 * @returns {number} The index, or -1 if not found
 */
export const search = (subject = "", pattern, fromIndex = 0) => {
  const str = toString(subject);
  const searchStr = fromIndex > 0 ? str.substring(fromIndex) : str;
  const result = searchStr.search(pattern);

  return result === -1 ? -1 : result + fromIndex;
};

/**
 * Inserts a string into subject at the specified position.
 * @param {string} [subject=''] - The string to insert into
 * @param {string} toInsert - The string to insert
 * @param {number} [position=0] - The position to insert at
 * @returns {string} The resulting string
 */
export const insert = (subject = "", toInsert, position = 0) => {
  const str = toString(subject);
  const ins = toString(toInsert);
  const pos = Math.max(0, Math.min(position, str.length));

  return str.slice(0, pos) + ins + str.slice(pos);
};

/**
 * Repeats subject a specified number of times.
 * @param {string} [subject=''] - The string to repeat
 * @param {number} [times=1] - The number of times to repeat
 * @returns {string} The repeated string
 */
export const repeat = (subject = "", times = 1) => {
  return toString(subject).repeat(Math.max(0, Math.floor(times)));
};

/**
 * Replaces matches in subject.
 * @param {string} [subject=''] - The string to search in
 * @param {string|RegExp} pattern - The pattern to match
 * @param {string|Function} replacement - The replacement
 * @returns {string} The resulting string
 */
export const replace = (subject = "", pattern, replacement = "") => {
  return toString(subject).replace(pattern, replacement);
};

/**
 * Replaces all matches in subject.
 * @param {string} [subject=''] - The string to search in
 * @param {string|RegExp} pattern - The pattern to match
 * @param {string|Function} replacement - The replacement
 * @returns {string} The resulting string
 */
export const replaceAll = (subject = "", pattern, replacement = "") => {
  const str = toString(subject);

  if (typeof pattern === "string") {
    return str.split(pattern).join(replacement);
  }

  // Ensure global flag for RegExp
  const flags = pattern.flags.includes("g") ? pattern.flags : pattern.flags + "g";
  const globalPattern = new RegExp(pattern.source, flags);

  return str.replace(globalPattern, replacement);
};

/**
 * Reverses subject.
 * @param {string} [subject=''] - The string to reverse
 * @returns {string} The reversed string
 */
export const reverse = (subject = "") => [...toString(subject)].reverse().join("");

/**
 * Reverses subject, handling graphemes correctly.
 * @param {string} [subject=''] - The string to reverse
 * @returns {string} The reversed string
 */
export const reverseGrapheme = (subject = "") => graphemes(subject).reverse().join("");

/**
 * Wraps subject to a specified line width.
 * @param {string} [subject=''] - The string to wrap
 * @param {Object} [options={}] - The wrap options
 * @param {number} [options.width=75] - The line width
 * @param {string} [options.newLine='\n'] - The newline string
 * @param {boolean} [options.indent=''] - Indentation for new lines
 * @returns {string} The wrapped string
 */
export const wordWrap = (subject = "", options = {}) => {
  const str = toString(subject);
  const { width = 75, newLine = "\n", indent = "" } = options;

  if (str.length <= width) return str;

  const regex = new RegExp(`(?![^\\n]{1,${width}}$)([^\\n]{1,${width}})\\s`, "g");

  return str.replace(regex, `$1${newLine}${indent}`);
};

/**
 * Splices subject, removing/inserting characters.
 * @param {string} [subject=''] - The string to splice
 * @param {number} start - The start index
 * @param {number} deleteCount - Number of characters to delete
 * @param {string} [toInsert=''] - String to insert
 * @returns {string} The spliced string
 */
export const splice = (subject = "", start, deleteCount, toInsert = "") => {
  const str = toString(subject);
  const startIndex = Math.max(0, start < 0 ? str.length + start : start);

  return str.slice(0, startIndex) + toInsert + str.slice(startIndex + Math.max(0, deleteCount));
};

/**
 * Escapes HTML special characters in subject.
 * @param {string} [subject=''] - The string to escape
 * @returns {string} The escaped string
 */
export const escapeHtml = (subject = "") => {
  const str = toString(subject);

  const htmlEntities = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };

  return str.replace(/[&<>"']/g, (char) => htmlEntities[char]);
};

/**
 * Unescapes HTML entities in subject.
 * @param {string} [subject=''] - The string to unescape
 * @returns {string} The unescaped string
 */
export const unescapeHtml = (subject = "") => {
  const str = toString(subject);

  const htmlEntities = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
    "&#x27;": "'",
  };

  return str.replace(/&(?:amp|lt|gt|quot|#39|#x27);/g, (entity) => htmlEntities[entity] || entity);
};

/**
 * Escapes RegExp special characters in subject.
 * @param {string} [subject=''] - The string to escape
 * @returns {string} The escaped string
 */
export const escapeRegExp = (subject = "") => {
  return toString(subject).replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
};

/**
 * Returns the character at the specified index.
 * @param {string} [subject=''] - The string to extract from
 * @param {number} [position=0] - The index
 * @returns {string} The character
 */
export const charAt = (subject = "", position = 0) => {
  const str = toString(subject);

  return str.charAt(position);
};

/**
 * Returns the first n characters of subject.
 * @param {string} [subject=''] - The string to extract from
 * @param {number} [length=1] - The number of characters
 * @returns {string} The first n characters
 */
export const first = (subject = "", length = 1) => {
  const str = toString(subject);
  const len = Math.max(0, Math.floor(length));

  return str.substring(0, len);
};

/**
 * Returns the last n characters of subject.
 * @param {string} [subject=''] - The string to extract from
 * @param {number} [length=1] - The number of characters
 * @returns {string} The last n characters
 */
export const last = (subject = "", length = 1) => {
  const str = toString(subject);
  const len = Math.max(0, Math.floor(length));

  return str.substring(str.length - len);
};

/**
 * Extracts a section of subject.
 * @param {string} [subject=''] - The string to extract from
 * @param {number} [start=0] - The start index
 * @param {number} [end] - The end index
 * @returns {string} The extracted string
 */
export const slice = (subject = "", start = 0, end) => {
  const str = toString(subject);

  return end === undefined ? str.slice(start) : str.slice(start, end);
};

/**
 * Extracts a substring from subject.
 * @param {string} [subject=''] - The string to extract from
 * @param {number} [start=0] - The start index
 * @param {number} [length] - The length to extract
 * @returns {string} The extracted string
 */
export const substr = (subject = "", start = 0, length) => {
  const str = toString(subject);
  const startIndex = start < 0 ? Math.max(0, str.length + start) : start;

  return length === undefined ? str.substring(startIndex) : str.substring(startIndex, startIndex + length);
};

/**
 * Returns a substring between two indices.
 * @param {string} [subject=''] - The string to extract from
 * @param {number} [start=0] - The start index
 * @param {number} [end] - The end index
 * @returns {string} The extracted string
 */
export const substring = (subject = "", start = 0, end) => {
  const str = toString(subject);

  return end === undefined ? str.substring(start) : str.substring(start, end);
};

/**
 * Formats a string using sprintf-style placeholders.
 * Supports: %s (string), %d/%i (integer), %f (float), %% (literal %)
 * @param {string} [format=''] - The format string
 * @param {...*} args - The arguments
 * @returns {string} The formatted string
 * @example
 * sprintf('%s costs $%.2f', 'Tea', 1.5) // => 'Tea costs $1.50'
 * sprintf('%d red %s', 3, 'apples') // => '3 red apples'
 */
export const sprintf = (format = "", ...args) => {
  const str = toString(format);
  let argIndex = 0;

  return str.replace(/%([+-]?\d*\.?\d*)([sdifox%])/g, (match, modifiers, type) => {
    if (type === "%") return "%";
    if (argIndex >= args.length) return match;

    const arg = args[argIndex++];
    const [, sign, width, precision] = modifiers.match(/^([+-]?)(\d*)(?:\.(\d+))?$/) || [];

    let result;

    switch (type) {
      case "s":
        result = toString(arg);
        break;
      case "d":
      case "i":
        result = parseInt(arg, 10).toString();
        break;
      case "f":
        result = precision !== undefined ? parseFloat(arg).toFixed(parseInt(precision, 10)) : parseFloat(arg).toString();
        break;
      case "o":
        result = parseInt(arg, 10).toString(8);
        break;
      case "x":
        result = parseInt(arg, 10).toString(16);
        break;
      default:
        result = arg;
    }

    if (width) {
      const padWidth = parseInt(width, 10);
      result = sign === "-" ? result.padEnd(padWidth) : result.padStart(padWidth);
    }

    return result;
  });
};

/**
 * Formats numbers with optional currency and decimal control.
 * @param {number|string} value - The value to format
 * @param {boolean} [useCurrency=false] - Whether to prefix with '$'
 * @param {number} [decimals=0] - Number of decimal places
 * @returns {string} The formatted number
 * @example
 * formatNumber(1234567) // => '1,234,567'
 * formatNumber(1234.56, true, 2) // => '$1,234.56'
 */
export const formatNumber = (value, useCurrency = false, decimals = 0) => {
  if (!isNumeric(value)) return toString(value);

  return new Intl.NumberFormat("en-US", {
    style: useCurrency ? "currency" : "decimal",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Helper: sanitizes an address to remove the part that points to the exact property location.
 * Returns a generalized location (e.g. Neighborhood, County, City, State, ZIP) without street/house number.
 * Not exported; used by formatAddress when masked=true.
 * ASSUMPTION: US-style address (street first, then city/state/zip). Single-segment (e.g. "Apt 2B") returns "".
 *
 * @param {string} address - The full address string
 * @returns {string} The generalized location (empty string if no address or single segment)
 */
const _generalizeAddress = (address) => {
  const str = toString(address);
  if (!str || !str.trim()) return "";

  const parts = str
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length > 1) {
    return parts.slice(1).join(", ");
  }

  return "";
};

/**
 * Returns the first comma-separated segment of a US-style full address (street line, often with unit).
 * Matches {@link _generalizeAddress}: that helper drops this segment; this one keeps it only.
 *
 * @param {string} address - Full address string
 * @returns {string} Trimmed street line, or empty when missing
 */
export const streetLineFromFullAddress = (address) => {
  const str = toString(address).trim();
  if (!str || str === ", ,") return "";

  const parts = str
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  return parts[0] ?? "";
};

/**
 * Formats a string as a clean mailing address.
 * Handles directions, state abbreviations, and ordinals.
 * When masked is true, removes house number and street and formats the generalized location only.
 *
 * @param {string} address - The address to format
 * @param {boolean} [masked=false] - When true, show only generalized location (no street/house number)
 * @returns {string} The formatted address
 * @example
 * formatAddress('123 N MAIN ST') // => '123 N Main St'
 * formatAddress('123 N Main St, Springfield, IL 62701', true) // => 'Springfield, IL 62701'
 * formatAddress('APT 2B') // => 'Apt 2B'
 */
export const formatAddress = (address, masked = false) => {
  const str = toString(address);

  if (!str || str.trim() === ", ,") return str;

  const toFormat = masked ? _generalizeAddress(str) : str;
  if (!toFormat || !toFormat.trim()) return "";

  return toFormat.toLowerCase().replace(/\b\w+\b/g, (word) => {
    // Force uppercase for directions, states, etc.
    if (FORCE_UPPER_CASE.has(word)) return word.toUpperCase();

    // Alphanumeric codes (except ordinals) should be uppercase
    const hasNumber = /\d/.test(word);
    const hasLetter = /[a-z]/.test(word);
    const isOrdinal = /^\d+(st|nd|rd|th)$/i.test(word);

    if (hasNumber && hasLetter && !isOrdinal) return word.toUpperCase();

    // Title case everything else
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
};

/**
 * Converts snake_case strings to Title Case, preserving hyphens.
 * Replaces underscores with spaces but preserves hyphens, then capitalizes each word.
 * SECURITY: Input validation prevents injection attacks. Uses stringUtils for safe manipulation.
 * @param {string} value - String value that may contain underscores or hyphens
 * @returns {string} Title case string with spaces instead of underscores, hyphens preserved
 */
export const formatTitleCase = (value) => {
  if (!isString(value) || isEmpty(value)) return "";
  // Replace underscores with spaces, then split by both spaces and hyphens
  // Process each segment separately to preserve hyphens
  let normalized = replaceAll(value, "_", " ");
  // Split by spaces, apply title case to each segment
  const spaceSegments = normalized.split(" ").map((segment) => {
    // For each segment, split by hyphens and capitalize each part
    return segment
      .split("-")
      .map((word) => capitalize(word, true))
      .join("-");
  });
  return spaceSegments.join(" ");
};

// Export internal helpers for testing/reuse
export { toString, isNil };
